import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PageContent, CisContainer, Spinner, Button, Row, Modal, Pagination } from 'components';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import { formatDateTime } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadMaMeetingsCreator,
  setMaMeetingsPageCreator,
  setMaMeetingsSortCreator,
  setMaMeetingsFiltersCreator,
  respondMaMeetingProxyCreator,
} from 'store/actionsCreators';
import MeetingsTable from '../Meetings.styled';

const LIST_TYPE = 'proxies';

/**
 * Proxy claims other Member Associations made towards this one: they state that
 * this MA votes on their behalf, and it is answered here.
 */
const Proxies = ({
  loading,
  list,
  loadList,
  setPage,
  setSort,
  setFilters,
  respondProxy,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const [confirmation, setConfirmation] = useState(null);

  // Permissions arrive after the first render, and until they do there is
  // nothing to deny yet - denying early would throw out a user who is allowed.
  // A user denied here may read neither meeting type, so there is no tab of
  // this section to move them to.
  const canView =
    !userPermissions ||
    checkPermissions({ roles: ['ma_meeting_view_cod', 'ma_meeting_view_csp'] }, userPermissions);
  const [allowed, renderAccessDeniedModal] = useAccessGuard(canView);

  useEffect(() => {
    if (!allowed) return;

    setPage(1);
    loadList();
  }, [sort, allowed]);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    setSort(direction ? `${key}.${direction}` : null);
  };

  const statusLabel = (item) => {
    if (item.approved === null || item.approved === undefined)
      return t('page.ma.meetings.proxies.statusPending');
    return item.approved
      ? t('page.ma.meetings.proxies.statusAccepted')
      : t('page.ma.meetings.proxies.statusRejected');
  };

  const renderActions = (item) => {
    if (!item.canRespond) return <span>-</span>;

    const acceptDisabled = !item.canAccept;

    return (
      <Row style={{ gap: '3px', justifyContent: 'center' }}>
        {item.approved !== true && (
          <Button
            style={{ width: 'auto' }}
            secondary
            disabled={acceptDisabled}
            title={
              acceptDisabled
                ? t('page.ma.meetings.proxies.limitReached', { max: item.maxAccepted })
                : undefined
            }
            onClick={() => setConfirmation({ item, approved: true })}
          >
            {t('page.ma.meetings.proxies.accept')}
          </Button>
        )}
        {item.approved !== false && (
          <Button
            style={{ width: 'auto' }}
            onClick={() => setConfirmation({ item, approved: false })}
          >
            {t('page.ma.meetings.proxies.reject')}
          </Button>
        )}
      </Row>
    );
  };

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  return (
    <PageContent>
      <CisContainer>
        <MeetingsTable tablebreakpoint="lg" actionsWidth="13rem">
          <MeetingsTable.Head>
            <MeetingsTable.Row>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.meetingName || ''}
                  onFilter={(val) => handleColumnFilterChange('meetingName', val)}
                  filterPlaceholder={t('page.ma.meetings.proxies.meetingPlaceholder')}
                  label={t('page.ma.meetings.proxies.meeting')}
                  sort
                  sortDirection={getSortDirection('meetingName')}
                  onSort={(dir) => handleColumnSort('meetingName', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.meetingType || ''}
                  onFilter={(val) => handleColumnFilterChange('meetingType', val)}
                  filterOptions={[
                    { value: 'COD', label: t('page.ma.meetings.proxies.typeCod') },
                    { value: 'CSP', label: t('page.ma.meetings.proxies.typeCsp') },
                  ]}
                  filterPlaceholder={t('page.ma.meetings.proxies.typePlaceholder')}
                  label={t('page.ma.meetings.proxies.type')}
                  sort
                  sortDirection={getSortDirection('meetingType')}
                  onSort={(dir) => handleColumnSort('meetingType', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.memberAssociation || ''}
                  onFilter={(val) => handleColumnFilterChange('memberAssociation', val)}
                  filterPlaceholder={t('page.ma.meetings.proxies.memberAssociationPlaceholder')}
                  label={t('page.ma.meetings.proxies.memberAssociation')}
                  sort
                  sortDirection={getSortDirection('memberAssociation')}
                  onSort={(dir) => handleColumnSort('memberAssociation', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.country || ''}
                  onFilter={(val) => handleColumnFilterChange('country', val)}
                  filterPlaceholder={t('page.ma.meetings.proxies.countryPlaceholder')}
                  label={t('page.ma.meetings.proxies.country')}
                  sort
                  sortDirection={getSortDirection('country')}
                  onSort={(dir) => handleColumnSort('country', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={[
                    { value: 'PENDING', label: t('page.ma.meetings.proxies.statusPending') },
                    { value: 'ACCEPTED', label: t('page.ma.meetings.proxies.statusAccepted') },
                    { value: 'REJECTED', label: t('page.ma.meetings.proxies.statusRejected') },
                  ]}
                  filterPlaceholder={t('page.ma.meetings.proxies.statusPlaceholder')}
                  label={t('page.ma.meetings.proxies.status')}
                  sort
                  sortDirection={getSortDirection('status')}
                  onSort={(dir) => handleColumnSort('status', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  label={t('page.ma.meetings.proxies.respondedAt')}
                  sort
                  sortDirection={getSortDirection('respondedAt')}
                  onSort={(dir) => handleColumnSort('respondedAt', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ma.meetings.proxies.actions')} isAction />
              </MeetingsTable.Heading>
            </MeetingsTable.Row>
          </MeetingsTable.Head>
          <MeetingsTable.Body>
            {loading ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={7}>
                  <Spinner />
                </MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={7}>{t('global.noResultsFound')}</MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => {
                  const meeting = item.meetingApplication ? item.meetingApplication.meeting : {};
                  const applicant = item.meetingApplication
                    ? item.meetingApplication.memberAssociation
                    : {};
                  const country = item.meetingApplication ? item.meetingApplication.country : null;

                  return (
                    <MeetingsTable.Row key={item.id}>
                      <MeetingsTable.Cell>
                        <MeetingsTable.Label>
                          {t('page.ma.meetings.proxies.meeting')}:
                        </MeetingsTable.Label>
                        {meeting.name}
                      </MeetingsTable.Cell>
                      <MeetingsTable.Cell>
                        <MeetingsTable.Label>
                          {t('page.ma.meetings.proxies.type')}:
                        </MeetingsTable.Label>
                        {meeting.type}
                      </MeetingsTable.Cell>
                      <MeetingsTable.Cell>
                        <MeetingsTable.Label>
                          {t('page.ma.meetings.proxies.memberAssociation')}:
                        </MeetingsTable.Label>
                        {applicant ? applicant.name : null}
                      </MeetingsTable.Cell>
                      <MeetingsTable.Cell>
                        <MeetingsTable.Label>
                          {t('page.ma.meetings.proxies.country')}:
                        </MeetingsTable.Label>
                        {country ? country.name : null}
                      </MeetingsTable.Cell>
                      <MeetingsTable.Cell>
                        <MeetingsTable.Label>
                          {t('page.ma.meetings.proxies.status')}:
                        </MeetingsTable.Label>
                        {statusLabel(item)}
                      </MeetingsTable.Cell>
                      <MeetingsTable.Cell>
                        <MeetingsTable.Label>
                          {t('page.ma.meetings.proxies.respondedAt')}:
                        </MeetingsTable.Label>
                        {formatDateTime(item.respondedAt) || '-'}
                      </MeetingsTable.Cell>
                      <MeetingsTable.Cell>
                        <MeetingsTable.Label>
                          {t('page.ma.meetings.proxies.actions')}:
                        </MeetingsTable.Label>
                        {renderActions(item)}
                      </MeetingsTable.Cell>
                    </MeetingsTable.Row>
                  );
                })
              : null}
          </MeetingsTable.Body>
        </MeetingsTable>
        <Pagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onChange={(payload) => {
            setPage(payload);
            loadList();
          }}
        />
      </CisContainer>
      <Modal
        heading={
          confirmation && confirmation.approved
            ? t('page.ma.meetings.proxies.confirmAccept')
            : t('page.ma.meetings.proxies.confirmReject')
        }
        isOpen={!!confirmation}
        confirm={() => {
          respondProxy({ id: confirmation.item.id, approved: confirmation.approved });
          setConfirmation(null);
        }}
        cancel={() => setConfirmation(null)}
        onRequestClose={() => setConfirmation(null)}
      />
    </PageContent>
  );
};

Proxies.propTypes = {
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  respondProxy: PropTypes.func.isRequired,
};

Proxies.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ma, user }) => ({
  loading: isLoading(loading, 'GLOBAL'),
  list: ma.meetings[LIST_TYPE],
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadMaMeetingsCreator(LIST_TYPE)),
  setPage: (payload) => dispatch(setMaMeetingsPageCreator(LIST_TYPE, payload)),
  setSort: (payload) => dispatch(setMaMeetingsSortCreator(LIST_TYPE, payload)),
  setFilters: (payload) => dispatch(setMaMeetingsFiltersCreator(LIST_TYPE, payload)),
  respondProxy: (payload) => dispatch(respondMaMeetingProxyCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Proxies);
